import React, { useState } from 'react';
import { Phone, PhoneIncoming, PhoneOutgoing, Clock, Users, TrendingUp, Bot, PhoneForwarded, DollarSign, BarChart3, Calendar, AlertCircle, CheckCircle2, TrendingDown, Zap } from 'lucide-react';
import { LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { useTheme } from 'contexts/ThemeContext';
import { getChartTheme, seriesColor, tooltipProps } from 'styles/chartTheme';
import { formatDurationSmart, formatNumberWithK } from 'utils/helpers';

interface BusinessDashboardProps {
  stats: any;
  dailyData: any[];
  hourlyData?: any[]; // Raw hourly data for heatmap
  pieData: any[];
  chartTitle?: string;
  showDetailedChart?: boolean;
}

const BusinessDashboard: React.FC<BusinessDashboardProps> = ({
  stats,
  dailyData,
  hourlyData = [],
  pieData,
  chartTitle = 'Call Volume - Last 7 Days',
  showDetailedChart = false
}) => {
  const { resolvedTheme } = useTheme();
  const chart = getChartTheme(resolvedTheme);
  const [activeTab, setActiveTab] = useState<'overview' | 'comparison'>('overview');
  const StatCard = ({ title, value, icon: Icon, color }: any) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{title}</p>
          <p className={`text-3xl font-bold ${color}`}>{value}</p>
        </div>
        <div className={`p-4 rounded-full ${color.replace('text', 'bg').replace('600', '100')}`}>
          <Icon className={`w-8 h-8 ${color}`} />
        </div>
      </div>
    </div>
  );

  // Component for 7-day comparison view
  const ComparisonView = () => {
    if (!dailyData || dailyData.length === 0) {
      return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
          <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">No daily data available for comparison</p>
        </div>
      );
    }

    // Calculate day-over-day changes
    const calculateChange = (current: number, previous: number) => {
      if (!previous) return null;
      const change = ((current - previous) / previous) * 100;
      return change;
    };

    // Get badge color based on change (for calls - increase is bad)
    const getBadge = (change: number | null, inverse: boolean = false) => {
      if (change === null) return null;
      const absChange = Math.abs(change);
      // For metrics where increase is good (inverse=true), flip the colors
      const color = inverse
        ? (change > 0 ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600')
        : (change > 0 ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600');
      return (
        <span className={`px-2 py-1 rounded text-xs font-semibold ${color}`}>
          {change > 0 ? '↑' : '↓'} {absChange.toFixed(1)}%
        </span>
      );
    };

    // Get badge for percentage values with thresholds
    const getPercentageBadge = (value: number, goodThreshold: number, warningThreshold: number) => {
      const color = value <= goodThreshold
        ? 'bg-green-100 text-green-600'
        : value <= warningThreshold
        ? 'bg-yellow-100 text-yellow-600'
        : 'bg-red-100 text-red-600';
      return (
        <span className={`px-2 py-1 rounded text-xs font-semibold ${color}`}>
          {value.toFixed(2)}%
        </span>
      );
    };

    return (
      <div className="space-y-6">
        {/* Summary Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {dailyData.map((day, index) => {
            const prevDay = index > 0 ? dailyData[index - 1] : null;
            const callChange = prevDay ? calculateChange(day.calls || 0, prevDay.calls || 0) : null;

            return (
              <div key={index} className="bg-white dark:bg-gray-800 border-l-4 border-cyan-600 rounded-lg shadow p-4 hover:shadow-lg transition">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-gray-900 dark:text-white">{day.day}</h3>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {day.date ? new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                  </span>
                </div>

                <div className="space-y-2">
                  <div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600 dark:text-gray-400">Total Calls</span>
                      {callChange !== null && getBadge(callChange)}
                    </div>
                    <div className="text-2xl font-bold text-cyan-600">{(day.calls || 0).toLocaleString()}</div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                    <div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">Incoming</div>
                      <div className="text-lg font-bold text-green-600">{(day.incoming || 0).toLocaleString()}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {day.calls > 0 ? `${Math.round((day.incoming / day.calls) * 100)}%` : '0%'}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">Outgoing</div>
                      <div className="text-lg font-bold text-blue-600">{(day.outgoing || 0).toLocaleString()}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {day.calls > 0 ? `${Math.round((day.outgoing / day.calls) * 100)}%` : '0%'}
                      </div>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="flex gap-1 h-2 rounded overflow-hidden bg-gray-100 dark:bg-gray-700">
                    <div
                      className="bg-green-500"
                      style={{ width: `${day.calls > 0 ? (day.incoming / day.calls) * 100 : 0}%` }}
                    />
                    <div
                      className="bg-blue-500"
                      style={{ width: `${day.calls > 0 ? (day.outgoing / day.calls) * 100 : 0}%` }}
                    />
                  </div>

                  {/* AI Metrics */}
                  <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                    <div>
                      <div className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1">
                        <Bot className="w-3 h-3" />
                        AI Calls
                      </div>
                      <div className="text-sm font-bold text-purple-600">{(day.totalAiCalls || 0).toLocaleString()}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {day.calls > 0 ? `${Math.round(((day.totalAiCalls || 0) / day.calls) * 100)}%` : '0%'}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1">
                        <PhoneForwarded className="w-3 h-3" />
                        Transfers
                      </div>
                      <div className="text-sm font-bold text-orange-600">{(day.totalTransfers || 0).toLocaleString()}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {day.calls > 0 ? `${Math.round(((day.totalTransfers || 0) / day.calls) * 100)}%` : '0%'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Detailed Metrics Comparison Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 bg-cyan-700">
            <h3 className="text-lg font-semibold text-white">📊 Detailed Performance Metrics</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase sticky left-0 bg-gray-50 dark:bg-gray-700/50">Metric</th>
                  {dailyData.map((day, index) => (
                    <th key={index} className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      {day.day}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Trend</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {/* Total Calls Row */}
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white sticky left-0 bg-white dark:bg-gray-800">Total Calls</td>
                  {dailyData.map((day, index) => {
                    const prevDay = index > 0 ? dailyData[index - 1] : null;
                    const change = prevDay ? calculateChange(day.calls || 0, prevDay.calls || 0) : null;
                    return (
                      <td key={index} className="px-4 py-3 text-right">
                        <div className="font-semibold text-gray-900 dark:text-white">{(day.calls || 0).toLocaleString()}</div>
                        {change !== null && (
                          <div className="text-xs mt-1">{getBadge(change)}</div>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 text-center">
                    {dailyData[0] && dailyData[dailyData.length - 1] && (
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        (dailyData[dailyData.length - 1].calls || 0) < (dailyData[0].calls || 0)
                          ? 'bg-red-100 text-red-600'
                          : 'bg-green-100 text-green-600'
                      }`}>
                        {(dailyData[dailyData.length - 1].calls || 0) < (dailyData[0].calls || 0) ? '↓ Declining' : '↑ Growing'}
                      </span>
                    )}
                  </td>
                </tr>

                {/* Incoming Calls Row */}
                {/* <tr className="hover:bg-gray-50 bg-green-50">
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 sticky left-0 bg-green-50">Incoming Calls</td>
                  {dailyData.map((day, index) => {
                    const prevDay = index > 0 ? dailyData[index - 1] : null;
                    const change = prevDay ? calculateChange(day.incoming || 0, prevDay.incoming || 0) : null;
                    return (
                      <td key={index} className="px-4 py-3 text-right">
                        <div className="font-semibold text-green-700">{(day.incoming || 0).toLocaleString()}</div>
                        {change !== null && (
                          <div className="text-xs mt-1">{getBadge(change)}</div>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 text-center">
                    <span className="px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-600">
                      {Math.round((stats.incomingCalls / stats.totalCalls) * 100)}% Avg
                    </span>
                  </td>
                </tr> */}

                {/* Outgoing Calls Row */}
                {/* <tr className="hover:bg-gray-50 bg-blue-50">
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 sticky left-0 bg-blue-50">Outgoing Calls</td>
                  {dailyData.map((day, index) => {
                    const prevDay = index > 0 ? dailyData[index - 1] : null;
                    const change = prevDay ? calculateChange(day.outgoing || 0, prevDay.outgoing || 0) : null;
                    return (
                      <td key={index} className="px-4 py-3 text-right">
                        <div className="font-semibold text-blue-700">{(day.outgoing || 0).toLocaleString()}</div>
                        {change !== null && (
                          <div className="text-xs mt-1">{getBadge(change)}</div>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 text-center">
                    <span className="px-2 py-1 rounded text-xs font-semibold bg-blue-100 text-blue-600">
                      {Math.round((stats.outgoingCalls / stats.totalCalls) * 100)}% Avg
                    </span>
                  </td>
                </tr> */}

                {/* AI Calls Row */}
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 bg-purple-50 dark:bg-purple-900/20">
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white sticky left-0 bg-purple-50 dark:bg-purple-900/20 flex items-center gap-2">
                    <Bot className="w-4 h-4 text-purple-600" />
                    AI Calls
                  </td>
                  {dailyData.map((day, index) => {
                    const aiCalls = day.totalAiCalls || 0;
                    const prevDay = index > 0 ? dailyData[index - 1] : null;
                    const change = prevDay ? calculateChange(aiCalls, prevDay.totalAiCalls || 0) : null;
                    return (
                      <td key={index} className="px-4 py-3 text-right">
                        <div className="font-semibold text-purple-700 dark:text-purple-400">{aiCalls.toLocaleString()}</div>
                        {change !== null && (
                          <div className="text-xs mt-1">{getBadge(change, true)}</div>
                        )}
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {day.calls > 0 ? `${Math.round((aiCalls / day.calls) * 100)}%` : '0%'}
                        </div>
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 text-center">
                    <span className="px-2 py-1 rounded text-xs font-semibold bg-purple-100 text-purple-600">
                      {dailyData.reduce((sum, day) => sum + (day.totalAiCalls || 0), 0).toLocaleString()}
                    </span>
                  </td>
                </tr>

                {/* AI Transferred Calls Row */}
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 bg-indigo-50 dark:bg-indigo-900/20">
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white sticky left-0 bg-indigo-50 dark:bg-indigo-900/20 flex items-center gap-2">
                    <PhoneForwarded className="w-4 h-4 text-indigo-600" />
                    AI Transferred Calls
                  </td>
                  {dailyData.map((day, index) => {
                    const aiTransfers = day.totalAiTransferredCalls || 0;
                    const prevDay = index > 0 ? dailyData[index - 1] : null;
                    const change = prevDay ? calculateChange(aiTransfers, prevDay.totalAiTransferredCalls || 0) : null;
                    return (
                      <td key={index} className="px-4 py-3 text-right">
                        <div className="font-semibold text-indigo-700 dark:text-indigo-400">{aiTransfers.toLocaleString()}</div>
                        {change !== null && (
                          <div className="text-xs mt-1">{getBadge(change)}</div>
                        )}
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {(day.totalAiCalls || 0) > 0 ? `${Math.round((aiTransfers / (day.totalAiCalls || 1)) * 100)}% of AI` : '0%'}
                        </div>
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 text-center">
                    <span className="px-2 py-1 rounded text-xs font-semibold bg-indigo-100 text-indigo-600">
                      {dailyData.reduce((sum, day) => sum + (day.totalAiTransferredCalls || 0), 0).toLocaleString()}
                    </span>
                  </td>
                </tr>

                {/* Answer Rate Row */}
                {/* <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 sticky left-0 bg-white">Answer Rate</td>
                  {dailyData.map((day, index) => {
                    const answerRate = day.calls > 0 ? ((day.calls - (day.missed || 0)) / day.calls) * 100 : 0;
                    return (
                      <td key={index} className="px-4 py-3 text-right">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          answerRate >= 90 ? 'bg-green-100 text-green-600' :
                          answerRate >= 80 ? 'bg-yellow-100 text-yellow-600' :
                          'bg-red-100 text-red-600'
                        }`}>
                          {answerRate.toFixed(1)}%
                        </span>
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      stats.missedCalls / stats.totalCalls < 0.1 ? 'bg-green-100 text-green-600' : 'bg-yellow-100 text-yellow-600'
                    }`}>
                      {((stats.totalCalls - stats.missedCalls) / stats.totalCalls * 100).toFixed(1)}%
                    </span>
                  </td>
                </tr> */}

                {/* Total Transfers Row */}
                {/* <tr className="hover:bg-gray-50 bg-orange-50">
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 sticky left-0 bg-orange-50">Total Transfers</td>
                  {dailyData.map((day, index) => {
                    const transfers = day.totalTransfers || 0;
                    const prevDay = index > 0 ? dailyData[index - 1] : null;
                    const change = prevDay ? calculateChange(transfers, prevDay.totalTransfers || 0) : null;
                    return (
                      <td key={index} className="px-4 py-3 text-right">
                        <div className="font-semibold text-orange-700">{transfers.toLocaleString()}</div>
                        {change !== null && (
                          <div className="text-xs mt-1">{getBadge(change)}</div>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 text-center">
                    <span className="px-2 py-1 rounded text-xs font-semibold bg-orange-100 text-orange-600">
                      {dailyData.reduce((sum, day) => sum + (day.totalTransfers || 0), 0).toLocaleString()}
                    </span>
                  </td>
                </tr> */}

                {/* Hot Key Transfers Row - Highlighted */}
                {/* <tr className="hover:bg-gray-50 bg-yellow-50 border-l-4 border-yellow-500">
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 sticky left-0 bg-yellow-50 flex items-center gap-2">
                    <Zap className="w-4 h-4 text-yellow-600" />
                    Hot Key Transfers
                  </td>
                  {dailyData.map((day, index) => {
                    const hotKeyTransfers = day.hotKeyTransfers || 0;
                    const prevDay = index > 0 ? dailyData[index - 1] : null;
                    const change = prevDay ? calculateChange(hotKeyTransfers, prevDay.hotKeyTransfers || 0) : null;
                    return (
                      <td key={index} className="px-4 py-3 text-right">
                        <div className="font-semibold text-yellow-700">{hotKeyTransfers.toLocaleString()}</div>
                        {change !== null && (
                          <div className="text-xs mt-1">{getBadge(change)}</div>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 text-center">
                    <span className="px-2 py-1 rounded text-xs font-semibold bg-yellow-100 text-yellow-700">
                      {dailyData.reduce((sum, day) => sum + (day.hotKeyTransfers || 0), 0).toLocaleString()}
                    </span>
                  </td>
                </tr> */}

                {/* Regular Transfers Row */}
                {/* <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 sticky left-0 bg-white">Regular Transfers</td>
                  {dailyData.map((day, index) => {
                    const regularTransfers = (day.totalTransfers || 0) - (day.hotKeyTransfers || 0);
                    const prevDay = index > 0 ? dailyData[index - 1] : null;
                    const prevRegular = prevDay ? (prevDay.totalTransfers || 0) - (prevDay.hotKeyTransfers || 0) : 0;
                    const change = prevDay ? calculateChange(regularTransfers, prevRegular) : null;
                    return (
                      <td key={index} className="px-4 py-3 text-right">
                        <div className="font-semibold text-gray-700">{regularTransfers.toLocaleString()}</div>
                        {change !== null && (
                          <div className="text-xs mt-1">{getBadge(change)}</div>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 text-center">
                    <span className="px-2 py-1 rounded text-xs font-semibold bg-gray-100 text-gray-600">
                      {dailyData.reduce((sum, day) => sum + ((day.totalTransfers || 0) - (day.hotKeyTransfers || 0)), 0).toLocaleString()}
                    </span>
                  </td>
                </tr> */}

                {/* AI Transfer Rate Row */}
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 bg-red-50 dark:bg-red-900/20">
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white sticky left-0 bg-red-50 dark:bg-red-900/20">AI Transfer Rate</td>
                  {dailyData.map((day, index) => {
                    const aiTransferRate = (day.totalAiCalls || 0) > 0 ? ((day.totalAiTransferredCalls || 0) / (day.totalAiCalls || 1)) * 100 : 0;
                    return (
                      <td key={index} className="px-4 py-3 text-right">
                        {getPercentageBadge(aiTransferRate, 8, 12)}
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 text-center">
                    {(() => {
                      const totalAiCalls = dailyData.reduce((sum, day) => sum + (day.totalAiCalls || 0), 0);
                      const totalAiTransfers = dailyData.reduce((sum, day) => sum + (day.totalAiTransferredCalls || 0), 0);
                      const avgRate = totalAiCalls > 0 ? (totalAiTransfers / totalAiCalls) * 100 : 0;
                      return getPercentageBadge(avgRate, 8, 12);
                    })()}
                  </td>
                </tr>

                {/* Actual IVR Transfer Rate Row */}
                {/* <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 sticky left-0 bg-white">Actual IVR Transfer Rate</td>
                  {dailyData.map((day, index) => {
                    const ivrTransferRate = day.calls > 0 ? ((day.ivrTransfers || 0) / day.calls) * 100 : 0;
                    return (
                      <td key={index} className="px-4 py-3 text-right">
                        {getPercentageBadge(ivrTransferRate, 5, 10)}
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 text-center">
                    {(() => {
                      const totalCalls = dailyData.reduce((sum, day) => sum + (day.calls || 0), 0);
                      const totalIvrTransfers = dailyData.reduce((sum, day) => sum + (day.ivrTransfers || 0), 0);
                      const avgRate = totalCalls > 0 ? (totalIvrTransfers / totalCalls) * 100 : 0;
                      return getPercentageBadge(avgRate, 5, 10);
                    })()}
                  </td>
                </tr> */}

                {/* Avg Duration AI Calls Row */}
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 bg-purple-50 dark:bg-purple-900/20">
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white sticky left-0 bg-purple-50 dark:bg-purple-900/20">Avg Duration AI Calls</td>
                  {dailyData.map((day, index) => {
                    const aiCalls = day.totalAiCalls || 0;
                    const aiSessionTime = day.totalAiSessionTimeMs || 0;
                    const avgAiDuration = aiCalls > 0 ? aiSessionTime / aiCalls / 1000 : 0; // Convert ms to seconds
                    const prevDay = index > 0 ? dailyData[index - 1] : null;
                    const prevAvgDuration = (prevDay?.totalAiCalls || 0) > 0
                      ? (prevDay?.totalAiSessionTimeMs || 0) / (prevDay?.totalAiCalls || 1) / 1000
                      : 0;
                    const change = prevDay && prevAvgDuration > 0 ? calculateChange(avgAiDuration, prevAvgDuration) : null;
                    return (
                      <td key={index} className="px-4 py-3 text-right">
                        <div className="font-semibold text-purple-700 dark:text-purple-400">{avgAiDuration.toFixed(2)}s</div>
                        {change !== null && (
                          <div className="text-xs mt-1">{getBadge(change)}</div>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 text-center">
                    <span className="px-2 py-1 rounded text-xs font-semibold bg-purple-100 text-purple-600">
                      {(() => {
                        const totalAiCalls = dailyData.reduce((sum, day) => sum + (day.totalAiCalls || 0), 0);
                        const totalAiSessionTime = dailyData.reduce((sum, day) => sum + (day.totalAiSessionTimeMs || 0), 0);
                        return totalAiCalls > 0 ? (totalAiSessionTime / totalAiCalls / 1000).toFixed(2) : '0.00';
                      })()}s
                    </span>
                  </td>
                </tr>

                {/* AI Resolution Rate Row */}
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 bg-green-50 dark:bg-green-900/20">
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white sticky left-0 bg-green-50 dark:bg-green-900/20">AI Resolution Rate</td>
                  {dailyData.map((day, index) => {
                    const aiCalls = day.totalAiCalls || 0;
                    const aiTransfers = day.totalAiTransferredCalls || 0;
                    const aiResolutionRate = aiCalls > 0 ? ((aiCalls - aiTransfers) / aiCalls) * 100 : 0;
                    return (
                      <td key={index} className="px-4 py-3 text-right">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          aiResolutionRate >= 90 ? 'bg-green-100 text-green-600' :
                          aiResolutionRate >= 85 ? 'bg-yellow-100 text-yellow-600' :
                          'bg-red-100 text-red-600'
                        }`}>
                          {aiResolutionRate.toFixed(2)}%
                        </span>
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 text-center">
                    {(() => {
                      const totalAiCalls = dailyData.reduce((sum, day) => sum + (day.totalAiCalls || 0), 0);
                      const totalAiTransfers = dailyData.reduce((sum, day) => sum + (day.totalAiTransferredCalls || 0), 0);
                      const avgResolution = totalAiCalls > 0 ? ((totalAiCalls - totalAiTransfers) / totalAiCalls) * 100 : 0;
                      return (
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          avgResolution >= 90 ? 'bg-green-100 text-green-600' :
                          avgResolution >= 85 ? 'bg-yellow-100 text-yellow-600' :
                          'bg-red-100 text-red-600'
                        }`}>
                          {avgResolution.toFixed(2)}%
                        </span>
                      );
                    })()}
                  </td>
                </tr>

                {/* Totals Row */}
                {/* <tr className="bg-gray-100 font-bold">
                  <td className="px-4 py-3 text-sm text-gray-900 sticky left-0 bg-gray-100">Period Totals</td>
                  {dailyData.map((day, index) => (
                    <td key={index} className="px-4 py-3 text-right text-cyan-600">
                      {(day.calls || 0).toLocaleString()}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-center text-cyan-600">
                    {dailyData.reduce((sum, day) => sum + (day.calls || 0), 0).toLocaleString()}
                  </td>
                </tr> */}
              </tbody>
            </table>
          </div>
        </div>

        {/* Performance Summary Statistics */}
        <div className="bg-cyan-700 rounded-lg shadow p-4 mb-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Performance Summary
          </h3>
          <p className="text-xs text-cyan-100 mt-1">Key metrics across the selected period</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Total Calls Summary Card */}
          <div className="bg-cyan-50 dark:from-cyan-900/30 dark:to-blue-900/30 rounded-lg p-6 border border-cyan-300 dark:border-cyan-700 shadow-md">
            <div className="flex items-center gap-3 mb-4">
              <Phone className="w-8 h-8 text-cyan-600" />
              <h4 className="font-semibold text-gray-900 dark:text-white text-lg">Total Calls</h4>
            </div>

            <div className="space-y-3">
              {/* Peak Day */}
              <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-800 rounded-lg">
                <div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 font-medium">Peak Day</div>
                  <div className="text-lg font-bold text-cyan-700 dark:text-cyan-400">
                    {dailyData.reduce((max, day) => day.calls > max.calls ? day : max, dailyData[0])?.day || 'N/A'}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-cyan-600">
                    {dailyData.reduce((max, day) => day.calls > max.calls ? day : max, dailyData[0])?.calls?.toLocaleString() || 0}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">calls</div>
                </div>
              </div>

              {/* Daily Average & Total Period */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-white dark:bg-gray-800 rounded-lg">
                  <div className="text-xs text-gray-600 dark:text-gray-400 font-medium">Daily Avg</div>
                  <div className="text-xl font-bold text-blue-700 dark:text-blue-400">
                    {Math.round(dailyData.reduce((sum, day) => sum + (day.calls || 0), 0) / dailyData.length).toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">per day</div>
                </div>
                <div className="p-3 bg-white dark:bg-gray-800 rounded-lg">
                  <div className="text-xs text-gray-600 dark:text-gray-400 font-medium">Total Period</div>
                  <div className="text-xl font-bold text-cyan-700 dark:text-cyan-400">
                    {dailyData.reduce((sum, day) => sum + (day.calls || 0), 0).toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{dailyData.length} days</div>
                </div>
              </div>
            </div>
          </div>

          {/* AI Calls Summary Card */}
          <div className="bg-purple-50 dark:from-purple-900/30 dark:to-indigo-900/30 rounded-lg p-6 border border-purple-300 dark:border-purple-700 shadow-md">
            <div className="flex items-center gap-3 mb-4">
              <Bot className="w-8 h-8 text-purple-600" />
              <h4 className="font-semibold text-gray-900 dark:text-white text-lg">AI Calls</h4>
            </div>

            <div className="space-y-3">
              {/* Peak Day */}
              <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-800 rounded-lg">
                <div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 font-medium">Peak Day</div>
                  <div className="text-lg font-bold text-purple-700 dark:text-purple-400">
                    {dailyData.reduce((max, day) => (day.totalAiCalls || 0) > (max.totalAiCalls || 0) ? day : max, dailyData[0])?.day || 'N/A'}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-purple-600">
                    {dailyData.reduce((max, day) => (day.totalAiCalls || 0) > (max.totalAiCalls || 0) ? day : max, dailyData[0])?.totalAiCalls?.toLocaleString() || 0}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">calls</div>
                </div>
              </div>

              {/* Daily Average & Total Period */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-white dark:bg-gray-800 rounded-lg">
                  <div className="text-xs text-gray-600 dark:text-gray-400 font-medium">Daily Avg</div>
                  <div className="text-xl font-bold text-indigo-700 dark:text-indigo-400">
                    {Math.round(dailyData.reduce((sum, day) => sum + (day.totalAiCalls || 0), 0) / dailyData.length).toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">per day</div>
                </div>
                <div className="p-3 bg-white dark:bg-gray-800 rounded-lg">
                  <div className="text-xs text-gray-600 dark:text-gray-400 font-medium">Total Period</div>
                  <div className="text-xl font-bold text-violet-700 dark:text-violet-400">
                    {dailyData.reduce((sum, day) => sum + (day.totalAiCalls || 0), 0).toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{dailyData.length} days</div>
                </div>
              </div>
            </div>
          </div>

          {/* AI Transfers Summary Card */}
          <div className="bg-orange-50 dark:from-orange-900/30 dark:to-red-900/30 rounded-lg p-6 border border-orange-300 dark:border-orange-700 shadow-md">
            <div className="flex items-center gap-3 mb-4">
              <PhoneForwarded className="w-8 h-8 text-orange-600" />
              <h4 className="font-semibold text-gray-900 dark:text-white text-lg">AI Transfers</h4>
            </div>

            <div className="space-y-3">
              {/* Peak Day */}
              <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-800 rounded-lg">
                <div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 font-medium">Peak Day</div>
                  <div className="text-lg font-bold text-orange-700 dark:text-orange-400">
                    {dailyData.reduce((max, day) => (day.totalAiTransferredCalls || 0) > (max.totalAiTransferredCalls || 0) ? day : max, dailyData[0])?.day || 'N/A'}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-orange-600">
                    {dailyData.reduce((max, day) => (day.totalAiTransferredCalls || 0) > (max.totalAiTransferredCalls || 0) ? day : max, dailyData[0])?.totalAiTransferredCalls?.toLocaleString() || 0}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">transfers</div>
                </div>
              </div>

              {/* Daily Average & Total Period */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-white dark:bg-gray-800 rounded-lg">
                  <div className="text-xs text-gray-600 dark:text-gray-400 font-medium">Daily Avg</div>
                  <div className="text-xl font-bold text-orange-700 dark:text-orange-400">
                    {Math.round(dailyData.reduce((sum, day) => sum + (day.totalAiTransferredCalls || 0), 0) / dailyData.length).toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">per day</div>
                </div>
                <div className="p-3 bg-white dark:bg-gray-800 rounded-lg">
                  <div className="text-xs text-gray-600 dark:text-gray-400 font-medium">Total Period</div>
                  <div className="text-xl font-bold text-red-700 dark:text-red-400">
                    {dailyData.reduce((sum, day) => sum + (day.totalAiTransferredCalls || 0), 0).toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{dailyData.length} days</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Call Duration Distribution */}
        {/* <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 bg-indigo-700">
            <h3 className="text-lg font-semibold text-white">📊 Call Duration Distribution</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration Bucket</th>
                  {dailyData.map((day, index) => (
                    <th key={index} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      {day.day}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Avg</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {[
                  { label: '< 5 seconds', key: 'duration_0_5', color: 'bg-red-50' },
                  { label: '5-30 seconds', key: 'duration_5_30', color: 'bg-orange-50' },
                  { label: '30-60 seconds', key: 'duration_30_60', color: 'bg-yellow-50' },
                  { label: '60-90 seconds', key: 'duration_60_90', color: 'bg-green-50' },
                  { label: '90-120 seconds', key: 'duration_90_120', color: 'bg-blue-50' },
                  { label: '> 120 seconds', key: 'duration_120_plus', color: 'bg-purple-50' }
                ].map((bucket, bucketIndex) => (
                  <tr key={bucketIndex} className={`hover:bg-gray-50 ${bucket.color}`}>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900">{bucket.label}</td>
                    {dailyData.map((day, index) => {
                      const count = (day.durationBuckets && day.durationBuckets[bucket.key]) || 0;
                      const percentage = day.calls > 0 ? (count / day.calls) * 100 : 0;
                      return (
                        <td key={index} className="px-4 py-3 text-center">
                          <div className="font-semibold text-gray-900">{count}</div>
                          <div className="text-xs text-gray-500">({percentage.toFixed(1)}%)</div>
                        </td>
                      );
                    })}
                    <td className="px-4 py-3 text-center">
                      {(() => {
                        const totalCount = dailyData.reduce((sum, day) => sum + ((day.durationBuckets && day.durationBuckets[bucket.key]) || 0), 0);
                        const avgCount = totalCount / dailyData.length;
                        return (
                          <div>
                            <div className="font-semibold text-gray-900">{Math.round(avgCount)}</div>
                            <div className="text-xs text-gray-500">avg/day</div>
                          </div>
                        );
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div> */}

        {/* Transfer Rate Trend Chart */}
        {/* <div className="bg-white rounded-lg shadow p-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">📈 Transfer Rate Trend</h3>
            <p className="text-sm text-gray-600">Daily transfer rate progression over the 7-day period</p>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={dailyData.map(day => ({
              day: day.day,
              transferRate: day.calls > 0 ? ((day.totalTransfers || 0) / day.calls) * 100 : 0,
              baseline: 8 // Target baseline
            }))}>
              <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
              <XAxis dataKey="day" stroke={chart.axis} tick={{ fill: chart.axis, fontSize: 12 }} />
              <YAxis stroke={chart.axis} tick={{ fill: chart.axis, fontSize: 12 }} label={{ value: 'Transfer Rate (%)', angle: -90, position: 'insideLeft', fill: chart.axis }} />
              <Tooltip {...tooltipProps(resolvedTheme)} formatter={(value: any) => `${Number(value).toFixed(2)}%`} />
              <Legend />
              <Line type="monotone" dataKey="baseline" stroke={chart.status.good} strokeDasharray="5 5" strokeWidth={2} name="Target (8%)" />
              <Line type="monotone" dataKey="transferRate" stroke={chart.status.critical} strokeWidth={3} name="Actual Transfer Rate" dot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </div> */}

        {/* Hot Key Transfer Analysis */}
        {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 bg-yellow-700">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Zap className="w-5 h-5" />
                Transfer Composition
              </h3>
            </div>
            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Day</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Regular</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Hot Key</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Hot Key %</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {dailyData.map((day, index) => {
                      const regular = (day.totalTransfers || 0) - (day.hotKeyTransfers || 0);
                      const hotKey = day.hotKeyTransfers || 0;
                      const hotKeyPct = day.totalTransfers > 0 ? (hotKey / day.totalTransfers) * 100 : 0;
                      return (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{day.day}</td>
                          <td className="px-4 py-3 text-center text-sm text-gray-700">{regular}</td>
                          <td className="px-4 py-3 text-center text-sm font-semibold text-yellow-600">{hotKey}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              hotKeyPct > 50 ? 'bg-red-100 text-red-600' :
                              hotKeyPct > 30 ? 'bg-yellow-100 text-yellow-600' :
                              'bg-green-100 text-green-600'
                            }`}>
                              {hotKeyPct.toFixed(1)}%
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 bg-yellow-700">
              <h3 className="text-lg font-semibold text-white">🔑 Key Findings</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-red-900 text-sm">High Hot Key Usage</h4>
                  <p className="text-xs text-red-700 mt-1">
                    Hot key transfers represent a significant portion of total transfers, indicating potential AI system limitations.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <TrendingUp className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-yellow-900 text-sm">Transfer Rate Above Target</h4>
                  <p className="text-xs text-yellow-700 mt-1">
                    Current transfer rate exceeds the 8% baseline, suggesting opportunities for AI improvement.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-blue-900 text-sm">Resolution Opportunity</h4>
                  <p className="text-xs text-blue-700 mt-1">
                    Reducing hot key transfers could significantly improve overall resolution rate and customer satisfaction.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div> */}

        {/* Hourly Peak Analysis - Multiple Heatmaps */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">🕐 Hourly Peak Analysis</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Distribution by hour across the selected period</p>
          </div>

          {(() => {
            // Calculate number of days in the period
            const uniqueDates = new Set(hourlyData.map(item => {
              try {
                return new Date(item.date).toISOString().split('T')[0];
              } catch {
                return '';
              }
            }));
            const numberOfDays = uniqueDates.size || 1;

            // Create a time key that includes both hour and minute for proper grouping
            // This handles timezones with 30-minute offsets (e.g., India UTC+5:30)
            const createTimeKey = (hour: number, minute: number) => `${hour}:${minute.toString().padStart(2, '0')}`;

            // Aggregate hourly data by time key (hour:minute)
            const callsMap = new Map<string, { hour: number; minute: number; value: number }>();
            const transfersMap = new Map<string, { hour: number; minute: number; value: number }>();
            const aiCallsMap = new Map<string, { hour: number; minute: number; value: number }>();

            hourlyData.forEach(item => {
              const timeKey = createTimeKey(item.hour, item.minute);

              if (!callsMap.has(timeKey)) {
                callsMap.set(timeKey, { hour: item.hour, minute: item.minute, value: 0 });
              }
              if (!transfersMap.has(timeKey)) {
                transfersMap.set(timeKey, { hour: item.hour, minute: item.minute, value: 0 });
              }
              if (!aiCallsMap.has(timeKey)) {
                aiCallsMap.set(timeKey, { hour: item.hour, minute: item.minute, value: 0 });
              }

              callsMap.get(timeKey)!.value += item.calls || 0;
              transfersMap.get(timeKey)!.value += item.totalTransfers || 0;
              aiCallsMap.get(timeKey)!.value += item.totalAiCalls || 0;
            });

            // Convert maps to sorted arrays
            const callsData = Array.from(callsMap.values()).sort((a, b) => {
              if (a.hour !== b.hour) return a.hour - b.hour;
              return a.minute - b.minute;
            });

            const transfersData = Array.from(transfersMap.values()).sort((a, b) => {
              if (a.hour !== b.hour) return a.hour - b.hour;
              return a.minute - b.minute;
            });

            const aiCallsData = Array.from(aiCallsMap.values()).sort((a, b) => {
              if (a.hour !== b.hour) return a.hour - b.hour;
              return a.minute - b.minute;
            });

            // Find max for scaling
            const maxCalls = Math.max(...callsData.map(h => h.value), 1);
            const maxTransfers = Math.max(...transfersData.map(h => h.value), 1);
            const maxAiCalls = Math.max(...aiCallsData.map(h => h.value), 1);

            // Helper functions
            const getColorIntensity = (value: number, max: number, colorScheme: 'blue' | 'orange' | 'purple') => {
              const intensity = value / max;
              if (intensity === 0) return 'bg-gray-100';

              const colors = {
                blue: ['bg-blue-100', 'bg-blue-200', 'bg-blue-300', 'bg-blue-400', 'bg-blue-500'],
                orange: ['bg-orange-100', 'bg-orange-200', 'bg-orange-300', 'bg-orange-400', 'bg-orange-500'],
                purple: ['bg-purple-100', 'bg-purple-200', 'bg-purple-300', 'bg-purple-400', 'bg-purple-500']
              };

              if (intensity < 0.2) return colors[colorScheme][0];
              if (intensity < 0.4) return colors[colorScheme][1];
              if (intensity < 0.6) return colors[colorScheme][2];
              if (intensity < 0.8) return colors[colorScheme][3];
              return colors[colorScheme][4];
            };

            const getTextColor = (value: number, max: number) => {
              const intensity = value / max;
              return intensity >= 0.6 ? 'text-white' : 'text-gray-900';
            };

            // Format time with hour and minute in 24-hour format - handles 30-min offsets correctly
            const formatTime = (hour: number, minute: number) => {
              return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
            };

            const formatTimeFull = (hour: number, minute: number) => {
              return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
            };

            // Find peak hours
            const peakCallHour = callsData.reduce((max, curr) => curr.value > max.value ? curr : max, callsData[0] || { hour: 0, minute: 0, value: 0 });
            const peakTransferHour = transfersData.reduce((max, curr) => curr.value > max.value ? curr : max, transfersData[0] || { hour: 0, minute: 0, value: 0 });
            const peakAiCallHour = aiCallsData.reduce((max, curr) => curr.value > max.value ? curr : max, aiCallsData[0] || { hour: 0, minute: 0, value: 0 });

            return (
              <div className="space-y-6">
                {/* Peak Hours Summary */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* 1. Normal Calls - Cyan/Blue */}
                  <div className="bg-cyan-50 rounded-lg p-4 border border-cyan-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-xs font-semibold text-cyan-900">Peak Call Hour</h4>
                        <p className="text-xl font-bold text-cyan-700 mt-1">{formatTimeFull(peakCallHour.hour, peakCallHour.minute)}</p>
                        <p className="text-xs text-gray-600 mt-1">
                          {peakCallHour.value.toLocaleString()} calls
                          <span className="text-gray-500"> (avg {(peakCallHour.value / numberOfDays).toFixed(1)}/day)</span>
                        </p>
                      </div>
                      <Phone className="w-10 h-10 text-cyan-400" />
                    </div>
                  </div>

                  {/* 2. AI Calls - Purple */}
                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-xs font-semibold text-purple-900">Peak AI Call Hour</h4>
                        <p className="text-xl font-bold text-purple-700 mt-1">{formatTimeFull(peakAiCallHour.hour, peakAiCallHour.minute)}</p>
                        <p className="text-xs text-gray-600 mt-1">
                          {peakAiCallHour.value.toLocaleString()} AI calls
                          <span className="text-gray-500"> (avg {(peakAiCallHour.value / numberOfDays).toFixed(1)}/day)</span>
                        </p>
                      </div>
                      <Bot className="w-10 h-10 text-purple-400" />
                    </div>
                  </div>

                  {/* 3. Transfers - Orange */}
                  <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-xs font-semibold text-orange-900">Peak Transfer Hour</h4>
                        <p className="text-xl font-bold text-orange-700 mt-1">{formatTimeFull(peakTransferHour.hour, peakTransferHour.minute)}</p>
                        <p className="text-xs text-gray-600 mt-1">
                          {peakTransferHour.value.toLocaleString()} transfers
                          <span className="text-gray-500"> (avg {(peakTransferHour.value / numberOfDays).toFixed(1)}/day)</span>
                        </p>
                      </div>
                      <PhoneForwarded className="w-10 h-10 text-orange-400" />
                    </div>
                  </div>
                </div>

                {/* 1. Total Calls Heatmap - Cyan/Blue */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <Phone className="w-4 h-4 text-cyan-600" />
                    Total Calls by Hour
                  </h4>
                  <div className="grid grid-cols-12 gap-2">
                    {callsData.map((timeData, index) => (
                      <div
                        key={`calls-${index}`}
                        className={`rounded-lg p-2 text-center transition-all hover:scale-105 hover:shadow-lg cursor-pointer ${getColorIntensity(timeData.value, maxCalls, 'blue')} ${getTextColor(timeData.value, maxCalls)}`}
                        title={`${formatTimeFull(timeData.hour, timeData.minute)}: ${timeData.value.toLocaleString()} calls`}
                      >
                        <div className="text-xs font-semibold">{formatTime(timeData.hour, timeData.minute)}</div>
                        <div className="text-xs font-bold mt-1">{timeData.value}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 2. AI Calls Heatmap - Purple */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <Bot className="w-4 h-4 text-purple-600" />
                    AI Calls by Hour
                  </h4>
                  <div className="grid grid-cols-12 gap-2">
                    {aiCallsData.map((timeData, index) => (
                      <div
                        key={`ai-${index}`}
                        className={`rounded-lg p-2 text-center transition-all hover:scale-105 hover:shadow-lg cursor-pointer ${getColorIntensity(timeData.value, maxAiCalls, 'purple')} ${getTextColor(timeData.value, maxAiCalls)}`}
                        title={`${formatTimeFull(timeData.hour, timeData.minute)}: ${timeData.value.toLocaleString()} AI calls`}
                      >
                        <div className="text-xs font-semibold">{formatTime(timeData.hour, timeData.minute)}</div>
                        <div className="text-xs font-bold mt-1">{timeData.value}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 3. Transfers Heatmap - Orange */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <PhoneForwarded className="w-4 h-4 text-orange-600" />
                    Transfers by Hour
                  </h4>
                  <div className="grid grid-cols-12 gap-2">
                    {transfersData.map((timeData, index) => (
                      <div
                        key={`transfers-${index}`}
                        className={`rounded-lg p-2 text-center transition-all hover:scale-105 hover:shadow-lg cursor-pointer ${getColorIntensity(timeData.value, maxTransfers, 'orange')} ${getTextColor(timeData.value, maxTransfers)}`}
                        title={`${formatTimeFull(timeData.hour, timeData.minute)}: ${timeData.value.toLocaleString()} transfers`}
                      >
                        <div className="text-xs font-semibold">{formatTime(timeData.hour, timeData.minute)}</div>
                        <div className="text-xs font-bold mt-1">{timeData.value}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Top 3 Busiest Hours for Each Metric */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {/* 1. Top 3 Call Hours - Cyan/Blue */}
                  <div className="bg-cyan-50 rounded-lg p-4 border border-cyan-200">
                    <h4 className="text-sm font-semibold text-cyan-900 mb-3 flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      Top 3 Call Hours
                    </h4>
                    <div className="space-y-2">
                      {callsData
                        .sort((a, b) => b.value - a.value)
                        .slice(0, 3)
                        .map((timeData, index) => (
                          <div key={`top-calls-${index}`} className="flex items-center justify-between p-2 bg-white rounded border border-cyan-200">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-bold text-gray-500 w-5">#{index + 1}</span>
                              <span className="text-sm font-semibold text-gray-900">{formatTimeFull(timeData.hour, timeData.minute)}</span>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-bold text-cyan-600">{timeData.value.toLocaleString()}</div>
                              <div className="text-xs text-gray-500">(avg {(timeData.value / numberOfDays).toFixed(1)}/day)</div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>

                  {/* 2. Top 3 AI Call Hours - Purple */}
                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                    <h4 className="text-sm font-semibold text-purple-900 mb-3 flex items-center gap-2">
                      <Bot className="w-4 h-4" />
                      Top 3 AI Call Hours
                    </h4>
                    <div className="space-y-2">
                      {aiCallsData
                        .sort((a, b) => b.value - a.value)
                        .slice(0, 3)
                        .map((timeData, index) => (
                          <div key={`top-ai-${index}`} className="flex items-center justify-between p-2 bg-white rounded border border-purple-200">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-bold text-gray-500 w-5">#{index + 1}</span>
                              <span className="text-sm font-semibold text-gray-900">{formatTimeFull(timeData.hour, timeData.minute)}</span>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-bold text-purple-600">{timeData.value.toLocaleString()}</div>
                              <div className="text-xs text-gray-500">(avg {(timeData.value / numberOfDays).toFixed(1)}/day)</div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>

                  {/* 3. Top 3 Transfer Hours - Orange */}
                  <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                    <h4 className="text-sm font-semibold text-orange-900 mb-3 flex items-center gap-2">
                      <PhoneForwarded className="w-4 h-4" />
                      Top 3 Transfer Hours
                    </h4>
                    <div className="space-y-2">
                      {transfersData
                        .sort((a, b) => b.value - a.value)
                        .slice(0, 3)
                        .map((timeData, index) => (
                          <div key={`top-transfers-${index}`} className="flex items-center justify-between p-2 bg-white rounded border border-orange-200">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-bold text-gray-500 w-5">#{index + 1}</span>
                              <span className="text-sm font-semibold text-gray-900">{formatTimeFull(timeData.hour, timeData.minute)}</span>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-bold text-orange-600">{timeData.value.toLocaleString()}</div>
                              <div className="text-xs text-gray-500">(avg {(timeData.value / numberOfDays).toFixed(1)}/day)</div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Key Insights */}
        {/* <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 bg-blue-700">
            <h3 className="text-lg font-semibold text-white">💡 Key Insights</h3>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="border-l-4 border-red-500 bg-red-50 p-4 rounded">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <h4 className="font-semibold text-red-900 text-sm">Critical</h4>
              </div>
              <p className="text-sm text-red-800">Transfer rate consistently above 10% threshold</p>
            </div>

            <div className="border-l-4 border-yellow-500 bg-yellow-50 p-4 rounded">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-5 h-5 text-yellow-600" />
                <h4 className="font-semibold text-yellow-900 text-sm">High Priority</h4>
              </div>
              <p className="text-sm text-yellow-800">Hot key transfers require immediate attention</p>
            </div>

            <div className="border-l-4 border-blue-500 bg-blue-50 p-4 rounded">
              <div className="flex items-center gap-2 mb-2">
                <TrendingDown className="w-5 h-5 text-blue-600" />
                <h4 className="font-semibold text-blue-900 text-sm">Trend Alert</h4>
              </div>
              <p className="text-sm text-blue-800">Resolution rate declining over the period</p>
            </div>

            <div className="border-l-4 border-purple-500 bg-purple-50 p-4 rounded">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-5 h-5 text-purple-600" />
                <h4 className="font-semibold text-purple-900 text-sm">Duration Impact</h4>
              </div>
              <p className="text-sm text-purple-800">Average call duration stable around 70s</p>
            </div>

            <div className="border-l-4 border-green-500 bg-green-50 p-4 rounded">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <h4 className="font-semibold text-green-900 text-sm">Positive Signal</h4>
              </div>
              <p className="text-sm text-green-800">Answer rate consistently above 85%</p>
            </div>

            <div className="border-l-4 border-indigo-500 bg-indigo-50 p-4 rounded">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-indigo-600" />
                <h4 className="font-semibold text-indigo-900 text-sm">Volume Trend</h4>
              </div>
              <p className="text-sm text-indigo-800">Call volume showing gradual increase</p>
            </div>
          </div>
        </div> */}

        {/* Action Plan & Recommendations */}
        {/* <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 bg-purple-700">
            <h3 className="text-lg font-semibold text-white">🎯 Action Plan & Recommendations</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Issue</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Impact</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recommended Action</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Timeline</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-semibold">HIGH</span>
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">Excessive Hot Key Transfers</td>
                  <td className="px-4 py-3 text-sm text-gray-700">Reduced AI effectiveness, poor user experience</td>
                  <td className="px-4 py-3 text-sm text-gray-700">Review and optimize AI conversation flows to reduce hot key reliance</td>
                  <td className="px-4 py-3 text-center text-sm">
                    <span className="px-2 py-1 bg-red-50 text-red-700 rounded text-xs">Immediate</span>
                  </td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs font-semibold">HIGH</span>
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">Transfer Rate Above Baseline</td>
                  <td className="px-4 py-3 text-sm text-gray-700">Lower resolution rate, increased operational costs</td>
                  <td className="px-4 py-3 text-sm text-gray-700">Analyze transfer reasons and enhance AI training data</td>
                  <td className="px-4 py-3 text-center text-sm">
                    <span className="px-2 py-1 bg-orange-50 text-orange-700 rounded text-xs">1-2 weeks</span>
                  </td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-semibold">MEDIUM</span>
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">Declining Resolution Rate</td>
                  <td className="px-4 py-3 text-sm text-gray-700">Customer satisfaction concerns, efficiency loss</td>
                  <td className="px-4 py-3 text-sm text-gray-700">Implement additional AI training and monitor call quality</td>
                  <td className="px-4 py-3 text-center text-sm">
                    <span className="px-2 py-1 bg-yellow-50 text-yellow-700 rounded text-xs">2-3 weeks</span>
                  </td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold">MEDIUM</span>
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">Call Duration Optimization</td>
                  <td className="px-4 py-3 text-sm text-gray-700">Efficiency opportunities, cost reduction potential</td>
                  <td className="px-4 py-3 text-sm text-gray-700">Streamline AI responses and reduce unnecessary interactions</td>
                  <td className="px-4 py-3 text-center text-sm">
                    <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">3-4 weeks</span>
                  </td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-semibold">LOW</span>
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">Peak Hour Staffing</td>
                  <td className="px-4 py-3 text-sm text-gray-700">Resource optimization opportunity</td>
                  <td className="px-4 py-3 text-sm text-gray-700">Analyze hourly patterns and adjust staffing accordingly</td>
                  <td className="px-4 py-3 text-center text-sm">
                    <span className="px-2 py-1 bg-green-50 text-green-700 rounded text-xs">1 month</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div> */}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-cyan-700 rounded-lg shadow p-6 text-white">
        <h1 className="text-3xl font-bold mb-2">Business Dashboard</h1>
        <p className="text-cyan-100">Your business performance at a glance</p>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'overview'
                  ? 'border-cyan-600 text-cyan-600'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              <BarChart3 className="w-4 h-4" />
              Overview
            </button>
            <button
              onClick={() => setActiveTab('comparison')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'comparison'
                  ? 'border-cyan-600 text-cyan-600'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              <Calendar className="w-4 h-4" />
              Day/Hourly Comparison
            </button>
          </nav>
        </div>
      </div>

      {/* Conditional Content based on active tab */}
      {activeTab === 'comparison' ? (
        <ComparisonView />
      ) : (
        <>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Calls"
          value={stats.totalCalls}
          icon={Phone}
          color="text-cyan-600"
        />
        <StatCard
          title="Incoming Calls"
          value={stats.incomingCalls}
          icon={PhoneIncoming}
          color="text-green-600"
        />
        <StatCard
          title="Outgoing Calls"
          value={stats.outgoingCalls}
          icon={PhoneOutgoing}
          color="text-blue-600"
        />
        <StatCard
          title="Missed Calls"
          value={stats.missedCalls}
          icon={Phone}
          color="text-red-600"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">{chartTitle}</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={dailyData}>
              <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
              <XAxis dataKey="day" stroke={chart.axis} tick={{ fill: chart.axis, fontSize: 12 }} />
              <YAxis stroke={chart.axis} tick={{ fill: chart.axis, fontSize: 12 }} />
              <Tooltip {...tooltipProps(resolvedTheme)} />
              {showDetailedChart && dailyData.length > 0 && dailyData[0].incoming !== undefined ? (
                <>
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="incoming"
                    stroke={chart.series[1]}
                    strokeWidth={2}
                    name="Incoming"
                  />
                  <Line
                    type="monotone"
                    dataKey="outgoing"
                    stroke={chart.series[0]}
                    strokeWidth={2}
                    name="Outgoing"
                  />
                </>
              ) : (
                <Line
                  type="monotone"
                  dataKey="calls"
                  stroke={chart.series[0]}
                  strokeWidth={2}
                  name="Total Calls"
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Call Type Distribution</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                dataKey="value"
              >
                {pieData.map((_, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={seriesColor(resolvedTheme, index)}
                    stroke={chart.tooltip.background}
                    strokeWidth={2}
                  />
                ))}
              </Pie>
              <Tooltip {...tooltipProps(resolvedTheme)} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {/* <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-2">Avg. Call Duration</h3>
          <div className="flex items-center gap-2">
            <Clock className="w-6 h-6 text-gray-600" />
            <span className="text-2xl font-bold">
              {formatDurationSmart(stats.avgDurationSeconds * 1000)}
            </span>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Average per call
          </p>
        </div> */}

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">Total Duration</h3>
          <div className="flex items-center gap-2">
            <Clock className="w-6 h-6 text-blue-600" />
            <span className="text-3xl font-bold text-blue-600">
              {formatNumberWithK(Math.floor((stats.totalCalls * stats.avgDurationSeconds) / 60))}
            </span>
            <span className="text-lg text-gray-600 dark:text-gray-400">minutes</span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {stats.totalCalls > 0
              ? `Avg: ${formatDurationSmart(Math.floor(stats.avgDurationSeconds) * 1000)} per call`
              : 'No calls'}
          </p>
        </div>

        {stats.totalAiCalls !== undefined && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">AI-Handled Calls</h3>
            <div className="flex items-center gap-2">
              <Bot className="w-6 h-6 text-purple-600" />
              <span className="text-2xl font-bold text-purple-600">
                {stats.totalAiCalls}
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              
              {stats.totalCalls > 0
                ? `${((stats.totalAiCalls / stats.totalCalls) * 100).toFixed(1)}% of total`
                : 'No calls'}
            </p>
          </div>
        )}

        {stats.totalAiTransferredCalls !== undefined && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">AI Transfers</h3>
            <div className="flex items-center gap-2">
              <PhoneForwarded className="w-6 h-6 text-orange-600" />
              <span className="text-2xl font-bold text-orange-600">
                {stats.totalAiTransferredCalls}
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {stats.totalAiCalls && stats.totalAiCalls > 0
                ? `${((stats.totalAiTransferredCalls / stats.totalAiCalls) * 100).toFixed(1)}% of AI calls`
                : 'Transferred by AI'}
            </p>
          </div>
        )}

        {stats.uniqueDialedNumbers !== undefined && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">AI Duration</h3>
            <div className="flex items-center gap-2">
              <Users className="w-6 h-6 text-indigo-600" />
              <span className="text-3xl font-bold text-purple-600">
                  {formatNumberWithK(Math.floor(stats.totalAiSessionTimeMs / 60000))}
                </span>
                <span className="text-lg text-gray-600 dark:text-gray-400">minutes</span>
            </div>
           <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {stats.totalAiCalls && stats.totalAiCalls > 0
                  ? `Avg: ${Math.floor(stats.totalAiSessionTimeMs / stats.totalAiCalls / 1000)}s per call`
                  : 'No AI calls'}
              </p>
          </div>
        )}
      </div>

      {/* AI Session Metrics - Only show if AI data is available */}
      {(stats.totalAiSessionTimeMs !== undefined || stats.totalBilledTimeMs !== undefined) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* {stats.totalAiSessionTimeMs !== undefined && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
                <Bot className="w-5 h-5 text-purple-600" />
                Total AI Session Time
              </h3>
              <div className="flex items-center gap-2">
                <span className="text-3xl font-bold text-purple-600">
                  {Math.floor(stats.totalAiSessionTimeMs / 60000)}
                </span>
                <span className="text-lg text-gray-600">minutes</span>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                {stats.totalAiCalls && stats.totalAiCalls > 0
                  ? `Avg: ${Math.floor(stats.totalAiSessionTimeMs / stats.totalAiCalls / 1000)}s per call`
                  : 'No AI calls'}
              </p>
            </div>
          )} */}

          {/* {stats.totalBilledTimeMs !== undefined && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                Total Billed Time
              </h3>
              <div className="flex items-center gap-2">
                <span className="text-3xl font-bold text-green-600">
                  {Math.floor(stats.totalBilledTimeMs / 60000)}
                </span>
                <span className="text-lg text-gray-600">minutes</span>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                {stats.totalCalls > 0
                  ? `Avg: ${Math.floor(stats.totalBilledTimeMs / stats.totalCalls / 1000)}s per call`
                  : 'No calls'}
              </p>
            </div>
          )} */}
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default BusinessDashboard;
